import { useMemo, useContext, useEffect, useState } from "react";
import axios from "axios";
import { AppContexts } from "../providers";

const LISTING_TEXT_KEYS = ["directions"];
const toStr = (v) => (v == null ? "" : String(v));

function prettyLabel(label) {
  const s = toStr(label).replace(/^\[|\]$/g, "");
  if (!s) return "";
  return s.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").trim();
}

async function safeGetListingText(listingTextKey) {
  try {
    const { data } = await axios.get(`/api/listing-text/${listingTextKey}`);
    const isNotFound =
      data?.success === false &&
      (data?.result?.error_code === 404 ||
        data?.result?.error_type === "record_not_found");
    if (isNotFound) return { key: listingTextKey, content: "" };
    return { key: listingTextKey, content: toStr(data?.result?.content).trim() };
  } catch (err) {
    if (err?.response?.status === 404) return { key: listingTextKey, content: "" };
    throw err;
  }
}

/**
 * Shared hook for loading property macro data for a given rentalId.
 * Returns { rows, loadingListingText } where rows are { id, label, rawLabel, value }.
 */
export default function usePropertyMacros(rentalId) {
  const { rental, getRental } = useContext(AppContexts.RentalsContext);
  const { settings, get } = useContext(AppContexts.SettingsContext);

  const [listingTextMap, setListingTextMap] = useState({});
  const [loadingListingText, setLoadingListingText] = useState(false);

  // Load macro definitions once
  useEffect(() => {
    get(["custom_macros"]);
  }, []);

  // Load rental when rentalId changes
  useEffect(() => {
    if (rentalId) getRental(rentalId);
  }, [rentalId]);

  // Listing text fetch
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!rentalId || !LISTING_TEXT_KEYS.length) return;
      setLoadingListingText(true);
      try {
        const settled = await Promise.allSettled(
          LISTING_TEXT_KEYS.map((k) => safeGetListingText(k))
        );
        if (cancelled) return;
        const map = {};
        for (const r of settled) {
          if (r.status === "fulfilled") map[r.value.key] = r.value.content;
        }
        setListingTextMap(map);
      } finally {
        if (!cancelled) setLoadingListingText(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [rentalId]);

  const mergedMacros = useMemo(() => {
    const rentalMacros = rental?.custom_macro_values || [];
    const customMacros = settings?.custom_macros || [];
    return customMacros.map((cm) => {
      const found = rentalMacros.find((m) => cm.id === m.macro_id);
      let content = toStr(found?.content).trim();
      const macroKey = toStr(cm?.macro_key || cm?.listingTextKey).trim();
      if (macroKey && listingTextMap[macroKey] != null) {
        content = toStr(listingTextMap[macroKey]).trim();
      }
      return { ...cm, content };
    });
  }, [settings?.custom_macros, rental?.custom_macro_values, listingTextMap]);

  const rows = useMemo(() => {
    return mergedMacros
      .map((m) => {
        const rawLabel = m?.macro ?? m?.name ?? m?.label ?? "";
        const label = prettyLabel(rawLabel) || toStr(rawLabel) || "Field";
        const value = toStr(m?.content).trim();
        return { id: m?.id ?? `${label}-${value}`, label, rawLabel: toStr(rawLabel), value };
      })
      .filter((r) => r.value.length > 0);
  }, [mergedMacros]);

  return { rows, loadingListingText };
}
